import React, { useLayoutEffect, useMemo, useState } from 'react';
import { App as AntdApp, ConfigProvider } from 'antd';
import { useLocation } from 'react-router-dom';

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const location = useLocation();
  const isResultsWithExternalScope =
    location.pathname === '/results' &&
    typeof location.search === 'string' &&
    new URLSearchParams(location.search).get('scope') === 'External';
  const isExternalTheme =
    location.pathname.startsWith('/external-projects') ||
    location.pathname.includes('/ext/') ||
    isResultsWithExternalScope;
  // Başlangıçta false: effect içinde RAF ile true yapılacak. true ile başlarsak
  // effect önce false yapıp sonra true yaptığı için ağaç unmount/remount oluyordu (çift istek kaynağı).
  const [themeReady, setThemeReady] = useState(false);

  const palette = useMemo(
    () =>
      isExternalTheme
        ? {
            accent: '#9B86BC',
            accentHover: '#AB96CC',
            accentActive: '#8A74AD',
            divider: '#6A5A86',
          }
        : {
            accent: '#00C271',
            accentHover: '#00B366',
            accentActive: '#00A35C',
            divider: '#00462C',
          },
    [isExternalTheme]
  );

  useLayoutEffect(() => {
    document.body.classList.toggle('external-theme', isExternalTheme);
    document.documentElement.classList.toggle('external-theme', isExternalTheme);
    const rafId = window.requestAnimationFrame(() => {
      setThemeReady(true);
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [isExternalTheme, location.pathname, palette.accent]);

  if (!themeReady) {
    return null;
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: palette.accent,
          colorBgBase: '#010B07',
          colorBgContainer: '#00130C',
          colorText: '#F6FFED',
          colorTextSecondary: '#666',
          colorBorder: 'transparent',
          colorLink: palette.accent,
          fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        },
        components: {
          Button: {
            colorPrimary: palette.accent,
            colorPrimaryHover: palette.accentHover,
            colorPrimaryActive: palette.accentActive,
            defaultShadow: 'none',
            primaryShadow: 'none',
            defaultBg: '#00130C',
            defaultBorderColor: palette.accent,
            defaultHoverBg: '#00130C',
            defaultHoverColor: palette.accent,
            defaultHoverBorderColor: palette.accent,
            defaultActiveBg: '#00130C',
            defaultActiveColor: palette.accent,
          },
          Layout: {
            bodyBg: '#010B07',
            headerBg: '#00130C',
          },
          Menu: {
            itemBg: '#00130C',
            itemColor: '#F6FFED',
            itemSelectedColor: palette.accent,
            itemSelectedBg: '#00130C',
            itemHoverBg: '#00130C',
          },
          Input: {
            colorBgContainer: '#00130C',
            colorText: '#F6FFED',
            colorTextPlaceholder: 'rgba(255, 255, 255, 0.3)',
            colorBorder: 'rgba(255, 255, 255, 0.3)',
            colorPrimary: palette.accent,
            colorPrimaryHover: palette.accentHover,
            hoverBorderColor: palette.accent,
            activeBorderColor: palette.accent,
            activeShadow: `0 0 0 2px rgba(${isExternalTheme ? '155, 134, 188' : '0, 194, 113'}, 0.2)`,
            colorError: '#ff4d4f',
            colorErrorBorder: 'rgba(255, 255, 255, 0.3)',
            colorErrorBg: 'rgba(255, 255, 255, 0.05)',
            colorBgContainerDisabled: 'rgba(255, 255, 255, 0.05)',
            colorTextDisabled: 'rgba(255, 255, 255, 0.3)',
          } as any,
          Select: {
            colorBgContainer: '#00130C',
            colorText: '#F6FFED',
            colorTextPlaceholder: 'rgba(255, 255, 255, 0.3)',
            colorBorder: 'rgba(255, 255, 255, 0.3)',
            colorPrimaryHover: palette.accentHover,
            colorPrimary: palette.accent,
            optionSelectedBg: palette.accent,
            optionSelectedColor: '#F6FFED',
            optionActiveBg: palette.accent,
            hoverBorderColor: palette.accent,
            activeBorderColor: palette.accent,
            activeOutlineColor: `rgba(${isExternalTheme ? '155, 134, 188' : '0, 194, 113'}, 0.2)`,
          } as any,
          
          DatePicker: {
            colorBgContainer: '#00130C',
            colorText: '#F6FFED',
            colorTextPlaceholder: 'rgba(255, 255, 255, 0.4)',
            colorBorder: 'rgba(255, 255, 255, 0.3)',
            colorPrimary: palette.accent,
            colorPrimaryHover: palette.accentHover,
            hoverBorderColor: palette.accent,
            activeBorderColor: palette.accent,
            activeShadow: `0 0 0 2px rgba(${isExternalTheme ? '155, 134, 188' : '0, 194, 113'}, 0.2)`,
            multipleItemBg: palette.accent,
            cellActiveWithRangeBg: palette.accent,
            cellRangeBorderColor: palette.accent,
          } as any,
          Typography: {
            colorLink: palette.accent,
          },
          
          Checkbox: {
            colorPrimary: palette.accent,
            colorBorder: 'rgba(255, 255, 255, 0.3)',
          },
          List: {
            colorSplit: palette.divider,
            colorBgContainer: 'transparent',
            colorText: '#F6FFED',
          },
          
        },
      }}
    >
      <AntdApp message={{ duration: 6 }}>{children}</AntdApp>
    </ConfigProvider>
  );
};
